import type { StaffBookingSummaryResponse } from "@repo/api-client";
import { toBookingStatus } from "../../bookings/status";

/** A booking still "counting" toward the day (not cancelled, not a no-show). */
const ACTIVE_STATUSES = new Set(["PENDING", "CONFIRMED", "SEATED", "COMPLETED"]);
/** Bookings the door is still expecting to walk in (upcoming arrivals list). */
const ARRIVAL_STATUSES = new Set(["PENDING", "CONFIRMED"]);

export interface TodaySummary {
  /** Total covers (sum of party sizes) across the day's active bookings. */
  covers: number;
  /** Count of active bookings today. */
  bookingCount: number;
  /** Count of parties still due to arrive at/after `nowSec`, chronologically. */
  upcomingCount: number;
  /** The next arrivals (soonest first), capped to `arrivalsLimit`. */
  nextArrivals: StaffBookingSummaryResponse[];
}

/**
 * Reduce today's booking rows into the overview dashboard's headline numbers — a pure function so the
 * landing screen's maths is unit-tested without React. Covers/counts ignore cancelled + no-show rows;
 * "next arrivals" are the not-yet-seated parties from `nowSec` onward, soonest first.
 */
export function summariseToday(
  rows: readonly StaffBookingSummaryResponse[],
  nowSec: number,
  arrivalsLimit = 6,
): TodaySummary {
  const active = rows.filter((r) => {
    const s = toBookingStatus(r.status);
    return s !== null && ACTIVE_STATUSES.has(s);
  });

  const covers = active.reduce(
    (sum, r) => sum + (typeof r.partySize === "number" ? r.partySize : 0),
    0,
  );

  const upcoming = active
    .filter((r) => {
      const s = toBookingStatus(r.status);
      return (
        s !== null &&
        ARRIVAL_STATUSES.has(s) &&
        typeof r.startSec === "number" &&
        r.startSec >= nowSec
      );
    })
    .toSorted((a, b) => (a.startSec ?? 0) - (b.startSec ?? 0));

  return {
    covers,
    bookingCount: active.length,
    upcomingCount: upcoming.length,
    nextArrivals: upcoming.slice(0, arrivalsLimit),
  };
}
